/**
 * Tax Export API — Chunk 116: Compliance Export Suite
 *                  Chunk 165: Tax Filing Export Integration
 *
 * Typed client for POST /analytics/tax/export (jurisdiction PDF/CSV)
 * and GET /analytics/tax/export/:userId (TurboTax TXF / H&R Block CSV / Koinly CSV).
 */

import apiClient from './client';
import type { SupportedCurrency } from '../store/slices/currencySlice';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TaxJurisdiction = 'US' | 'UK' | 'AU' | 'EU_DE' | 'EU_FR' | 'EU_OTHER';

export type TaxExportFormat = 'irs_8949' | 'hmrc_cgt' | 'ato_cgt' | 'csv';

/** Filing-software export formats (Chunk 165) */
export type FilingExportFormat = 'turbotax' | 'hrblock' | 'koinly';
export type TaxExportSupportedCurrency = SupportedCurrency;
export type TaxExportSummaryFormat = 'form8949' | 'turbotax' | 'generic';
export type TaxExportLifecycleState =
  | 'preparing'
  | 'generating'
  | 'slow'
  | 'ready'
  | 'failed'
  | 'empty';
export type DisplayCurrencySource = 'display_currency' | 'preferred_currency' | 'usd_fallback';

export interface TaxExportOperationStatus {
  state: TaxExportLifecycleState;
  format: TaxExportSummaryFormat | 'summary' | 'dataset';
  userId: string;
  year: number;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  disposalCount?: number;
  hasDisposals?: boolean;
  fromCache?: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
  requestedCurrency?: TaxExportSupportedCurrency;
  appliedCurrency?: TaxExportSupportedCurrency;
  defaultCurrency?: TaxExportSupportedCurrency;
  fileName?: string;
  sharedDatasetReady?: boolean;
  slowThresholdMs: number;
}

export interface TaxExportStatusSnapshot {
  userId: string;
  year: number;
  defaultCurrency: TaxExportSupportedCurrency;
  effectiveDisplayCurrency: TaxExportSupportedCurrency;
  currencySource: DisplayCurrencySource;
  monetaryBaseCurrency: 'USD';
  displayCurrencyConversionAvailable: boolean;
  displayCurrencyConversionRate: number | null;
  dataset: TaxExportOperationStatus | null;
  exports: Partial<Record<'summary' | TaxExportSummaryFormat, TaxExportOperationStatus>>;
  availableStates: TaxExportLifecycleState[];
  rawValueMarker: 'USD_BASE_VALUES';
}

export interface TaxExportSummary {
  userId: string;
  year: number;
  disposalCount: number;
  hasDisposals: boolean;
  defaultCurrency: TaxExportSupportedCurrency;
  supportedFormats: TaxExportSummaryFormat[];
  effectiveDisplayCurrency?: TaxExportSupportedCurrency;
  currencySource?: DisplayCurrencySource;
  monetaryBaseCurrency?: 'USD';
  rawValueMarker?: 'USD_BASE_VALUES';
  displayCurrencyConversionAvailable?: boolean;
  displayCurrencyConversionRate?: number | null;
  datasetStatus?: TaxExportOperationStatus | null;
  statusStates?: TaxExportLifecycleState[];
}

export interface DownloadTaxExportResult {
  blob: Blob;
  fileName: string | null;
  exportState?: TaxExportLifecycleState;
  disposalCount?: number;
  statusPath?: string;
  appliedCurrency?: TaxExportSupportedCurrency;
}

export type CostBasisMethod = 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE';

export interface TaxExportRequest {
  userId: string;
  taxYear: number;
  format: TaxExportFormat;
  method?: CostBasisMethod;
}

export interface TaxFormOption {
  format: TaxExportFormat;
  label: string;
  contentType: 'pdf' | 'csv';
}

// ─── Jurisdiction → available formats ────────────────────────────────────────

export const JURISDICTION_FORM_OPTIONS: Record<TaxJurisdiction, TaxFormOption[]> = {
  US: [
    { format: 'irs_8949', label: 'IRS Form 8949 (PDF)', contentType: 'pdf' },
    { format: 'csv', label: 'CSV Fallback', contentType: 'csv' },
  ],
  UK: [
    { format: 'hmrc_cgt', label: 'HMRC CGT Summary (PDF)', contentType: 'pdf' },
    { format: 'csv', label: 'CSV Fallback', contentType: 'csv' },
  ],
  AU: [
    { format: 'ato_cgt', label: 'ATO CGT Report (PDF)', contentType: 'pdf' },
    { format: 'csv', label: 'CSV Fallback', contentType: 'csv' },
  ],
  EU_DE: [{ format: 'csv', label: 'Tax Summary CSV', contentType: 'csv' }],
  EU_FR: [{ format: 'csv', label: 'Tax Summary CSV', contentType: 'csv' }],
  EU_OTHER: [{ format: 'csv', label: 'Tax Summary CSV', contentType: 'csv' }],
};

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * Request a tax form export (jurisdiction PDF/CSV). Returns a Blob for download.
 * Handles both PDF and CSV content types.
 */
export async function exportTaxForm(params: TaxExportRequest): Promise<Blob> {
  const response = await apiClient.post<Blob>('/analytics/tax/export', params, {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Download a tax filing export for TurboTax (TXF), H&R Block (CSV), or Koinly (CSV).
 * Returns a Blob representing the file content.
 */
export async function downloadTaxExport(
  userId: string,
  format: FilingExportFormat,
  year: number,
): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    `/analytics/tax/export/${encodeURIComponent(userId)}`,
    {
      params: { format, year },
      responseType: 'blob',
    },
  );
  return response.data;
}

export async function getTaxExportSummary(
  userId: string,
  year: number,
): Promise<TaxExportSummary> {
  const response = await apiClient.get<TaxExportSummary>(
    `/analytics/tax/export/${encodeURIComponent(userId)}/summary`,
    {
      params: { year },
    },
  );
  return response.data;
}

export async function getTaxExportStatus(
  userId: string,
  year: number,
  currency?: TaxExportSupportedCurrency,
): Promise<TaxExportStatusSnapshot> {
  const response = await apiClient.get<TaxExportStatusSnapshot>(
    `/analytics/tax/export/${encodeURIComponent(userId)}/status`,
    {
      params: currency ? { year, currency } : { year },
    },
  );
  return response.data;
}

function parseHeaderNumber(value: unknown): number | undefined {
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseContentDispositionFilename(header: unknown): string | null {
  if (typeof header !== 'string' || header.trim() === '') {
    return null;
  }

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const bareMatch = header.match(/filename=([^;]+)/i);
  return bareMatch?.[1]?.trim() ?? null;
}

function parseDownloadResponse(response: {
  data: Blob;
  headers?: Record<string, unknown>;
}): DownloadTaxExportResult {
  return {
    blob: response.data,
    fileName: parseContentDispositionFilename(response.headers?.['content-disposition']),
    exportState:
      typeof response.headers?.['x-export-state'] === 'string'
        ? (response.headers['x-export-state'] as TaxExportLifecycleState)
        : undefined,
    disposalCount: parseHeaderNumber(response.headers?.['x-export-disposal-count']),
    statusPath:
      typeof response.headers?.['x-export-status-path'] === 'string'
        ? response.headers['x-export-status-path']
        : undefined,
    appliedCurrency:
      typeof response.headers?.['x-export-currency'] === 'string'
        ? (response.headers['x-export-currency'] as TaxExportSupportedCurrency)
        : undefined,
  };
}

export async function downloadForm8949(
  userId: string,
  year: number,
): Promise<DownloadTaxExportResult> {
  const response = await apiClient.get<Blob>(
    `/analytics/tax/export/${encodeURIComponent(userId)}/form8949`,
    {
      params: { year },
      responseType: 'blob',
    },
  );
  return parseDownloadResponse(response);
}

export async function downloadTurboTaxExport(
  userId: string,
  year: number,
): Promise<DownloadTaxExportResult> {
  const response = await apiClient.get<Blob>(
    `/analytics/tax/export/${encodeURIComponent(userId)}/turbotax`,
    {
      params: { year },
      responseType: 'blob',
    },
  );
  return parseDownloadResponse(response);
}

export async function downloadGenericExport(
  userId: string,
  year: number,
  currency: TaxExportSupportedCurrency,
): Promise<DownloadTaxExportResult> {
  const response = await apiClient.get<Blob>(
    `/analytics/tax/export/${encodeURIComponent(userId)}/generic`,
    {
      params: { year, currency },
      responseType: 'blob',
    },
  );
  return parseDownloadResponse(response);
}

/**
 * Trigger a browser download from a Blob.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build a sensible filename for the export.
 */
export function buildExportFilename(format: TaxExportFormat, taxYear: number): string {
  const ext = format === 'csv' ? 'csv' : 'pdf';
  const nameMap: Record<TaxExportFormat, string> = {
    irs_8949: `irs-form-8949-${taxYear}`,
    hmrc_cgt: `hmrc-cgt-summary-${taxYear}`,
    ato_cgt: `ato-cgt-report-${taxYear}`,
    csv: `tax-export-${taxYear}`,
  };
  return `${nameMap[format]}.${ext}`;
}

/**
 * Build filename for a filing-software export (Chunk 165).
 */
export function buildFilingExportFilename(format: FilingExportFormat, taxYear: number): string {
  const extMap: Record<FilingExportFormat, string> = {
    turbotax: 'txf',
    hrblock: 'csv',
    koinly: 'csv',
  };
  return `${format}-tax-export-${taxYear}.${extMap[format]}`;
}
